/**
 * MODO DEMO — "DEMO OPUS ai": recorrido INTERACTIVO con datos estáticos.
 *
 * Se activa al compilar con VITE_DEMO=1 (script `build:demo`). Produce un único
 * .html sin backend que reproduce lo que hace internamente OPUS ai con
 * resultados PRE-CALCULADOS: no consume tokens. Se publica dentro de
 * opus.com.mx (carpeta /demo-ai/) para demostraciones.
 *
 * A diferencia de la primera versión (una foto terminada), el visitante EJECUTA
 * el flujo completo guiado por el Asistente Ecosoft (AsistenteEcosoft.tsx):
 *
 *   crear proyecto → subir el PDF → generar resumen → preguntar al chat →
 *   ajustar contexto → armar la hoja → elegir catálogos → buscar equivalentes →
 *   generar precios elaborados con IA → ver cómo lo pensó la IA → (final) se explica
 *   que el presupuesto se importa desde OPUS 27 en la computadora del usuario
 *
 * Cómo funciona:
 *  - `apiFetch` se corta acá y devuelve fixtures QUE DEPENDEN DE LA ETAPA del
 *    recorrido (tourStore). Los POST del visitante avanzan la etapa.
 *  - Los procesos largos (leer PDF, resumen, armar hoja, equivalentes, APUs)
 *    se animan emitiendo por `tourBus` los MISMOS eventos ProjectEvent del
 *    worker real: las pantallas ya saben pintarlos (barras, contadores, toasts).
 *  - El chat responde con un stream falso (chatDemo.ts) sobre la UI real.
 *
 * Los datos viven en `casaDemo.json` (generado por `scripts/gen-demo-casa.mjs`):
 * el ejercicio del curso — casa habitación de un nivel, 65.00 m², Santiago de
 * Querétaro — con la textura de una corrida REAL de OPUS ai.
 *
 * NO afecta el build normal (todo gateado por la constante DEMO).
 */
import type { LoginSuccess } from '../api/auth.js';
import type { ProjectFile } from '../api/projects.js';
import casa from './casaDemo.json';
import { tour, TOUR } from './tourStore.js';
import { demoBusPlay, demoBusEmit } from './tourBus.js';
import { demoChatLog } from './chatDemo.js';

export const DEMO: boolean = import.meta.env.VITE_DEMO === '1';

const TENANT_ID = casa.ids.TENANT_ID;
const DEMO_USER_ID = casa.ids.USER_ID;
const ECOSTOS_ID = casa.ids.ECOSTOS_ID;
const PRJ = casa.ids.PRJ;
const QUOTE = casa.ids.QUOTE;
const RUN = casa.ids.RUN;
const FILE_PDF = casa.ids.FILE_PDF;

const iso = '2026-08-20T16:30:00Z';

export const DEMO_SESSION: LoginSuccess = {
  user: {
    id: DEMO_USER_ID,
    email: 'visitante@demo.opus.com.mx',
    username: 'visitante.demo',
    name: 'Visitante de demostración',
    avatarUrl: null,
  },
  // Tenant de CLIENTE (no Ecosoft): el demo no muestra vistas internas /staff.
  tenant: { id: TENANT_ID, slug: 'constructora-demo', name: 'Constructora Demo MX', status: 'active', role: 'owner', isEcosoftOwned: false },
  programs: [
    { id: 'p-1', programId: 'supernova', status: 'active', features: { 100: true }, seats: 5, startedAt: '2026-01-01T00:00:00Z', expiresAt: '2027-01-01T00:00:00Z' },
  ],
  availableTenants: [{ id: TENANT_ID, slug: 'constructora-demo', name: 'Constructora Demo MX', role: 'owner' }],
  subscriptionExpiresAt: '2027-01-01T00:00:00Z',
  subscriptionExpired: false,
  qaModeEnabled: false,
  // OJO: demoGuest en FALSE a propósito — el recorrido es interactivo (crear,
  // subir, generar). El candado de la demostración es el resolver: nada sale a la red.
  demoGuest: false,
  productUrl: 'https://apps.opus.com.mx',
};

// ── Estado efímero del recorrido (module-level; el duradero va en tourStore) ──
/** Archivo "subido" mientras corre la animación de lectura (antes de RESUMEN). */
let uploadedFile: (ProjectFile & Record<string, unknown>) | null = null;
/** Toggles de IA que el visitante prendió/apagó a mano. */
const iaOverrides: Record<string, boolean> = {};

type Row = Record<string, unknown> & {
  id: string; idx: number; kind: string; confianza: string;
  importe: number; parentIdx: number | null; insumos: unknown[];
};

const ALL_ROWS = casa.concepts as unknown as Row[];
const round2 = (n: number) => Math.round(n * 100) / 100;

function isSintetico(c: Row): boolean { return c.confianza === 'sintetica'; }

/** Renglón recién extraído: sin precio, sin fuente, sin procesar. */
function sinPrecio(c: Row): Row {
  return {
    ...c,
    precioUnitario: 0, importe: 0, confianza: 'sin_candidato', fuente: null,
    nota: null, catalogIdOrigen: null, claveCatalogo: null, descripcionCatalogo: null,
    unidadCatalogo: null, score: null, insumos: [], status: 'pending', processedAt: null,
    apuIaJobRunId: null, apuIaReviewStatus: null, hasApuIaMatrix: false,
    apuIaRequested: null, precioCatalogoGuardado: null, importeCatalogoGuardado: null,
    precioIa: null, importeIa: null,
  };
}

/** Renglón que la búsqueda procesó y NO encontró equivalente (toggle IA ON por default). */
function sinEquivalente(c: Row): Row {
  return { ...sinPrecio(c), status: 'pending', processedAt: iso };
}

/** Recalcula el importe de las partidas sumando SOLO los hijos con precio. */
function conTotalesDeGrupo(rows: Row[]): Row[] {
  const byIdx = new Map(rows.map((r) => [r.idx, r]));
  const totals = new Map<number, number>();
  for (const r of rows) {
    if (r.kind === 'group') continue;
    let p = r.parentIdx; let hops = 0;
    while (p != null && hops++ < 14) {
      totals.set(p, (totals.get(p) ?? 0) + (Number(r.importe) || 0));
      p = (byIdx.get(p)?.parentIdx ?? null) as number | null;
    }
  }
  return rows.map((r) => (r.kind === 'group' ? { ...r, importe: round2(totals.get(r.idx) ?? 0) } : r));
}

/** Conceptos según la etapa (y los ticks de las animaciones en curso). */
function conceptsForStage(): Row[] {
  const stage = tour.stage;
  if (stage <= TOUR.ARMAR) return [];
  const rows = ALL_ROWS.map((c) => ({ ...c }));
  const matched = rows.filter((c) => c.kind !== 'group' && !isSintetico(c));
  const sints = rows.filter((c) => c.kind !== 'group' && isSintetico(c));
  let out: Row[];
  if (stage === TOUR.CATALOGOS || stage === TOUR.EQUIVALENTES) {
    // Hoja armada sin precios. Durante "Buscar equivalentes" los matches se
    // van llenando en vivo (pricedTick lo mueve la secuencia de eventos).
    const nPriced = stage === TOUR.EQUIVALENTES ? tour.pricedTick : 0;
    const pricedIds = new Set(matched.slice(0, nPriced).map((c) => c.id));
    out = rows.map((c) => {
      if (c.kind === 'group') return c;
      if (pricedIds.has(c.id)) return c;       // valores finales del match
      return sinPrecio(c);
    });
  } else if (stage === TOUR.SINTETICOS) {
    // 29 con precio de catálogo; los 6 sin equivalente esperan el paso 3.
    // Durante la generación, los primeros apuTick ya tienen matriz.
    const doneIds = new Set(sints.slice(0, tour.apuTick).map((c) => c.id));
    out = rows.map((c) => {
      if (c.kind === 'group' || !isSintetico(c)) return c;
      if (doneIds.has(c.id)) return { ...c, apuIaReviewStatus: 'approved' };
      return sinEquivalente(c);
    });
  } else {
    out = rows; // EXPORTAR / FIN: hoja completa
  }
  out = out.map((c) => (iaOverrides[c.id] !== undefined ? { ...c, apuIaRequested: iaOverrides[c.id] } : c));
  return conTotalesDeGrupo(out);
}

function totalsForRows(rows: Row[]): { priced: number; amount: number } {
  let priced = 0; let amount = 0;
  for (const r of rows) {
    if (r.kind === 'group') continue;
    const pu = Number(r.precioUnitario) || 0;
    if (pu > 0) { priced++; amount += Number(r.importe) || 0; }
  }
  return { priced, amount: round2(amount) };
}

function quoteForStage(): Record<string, unknown> {
  const stage = tour.stage;
  const base = {
    ...(casa.quote as Record<string, unknown>),
    isExample: false,
    catalogIds: tour.catalogIds,
    name: (tour.projectName ? `${tour.projectName} — presupuesto` : (casa.quote as Record<string, unknown>).name) as string,
  };
  if (stage <= TOUR.ARMAR) {
    return {
      ...base, status: 'draft', conceptsTotal: 0, conceptsProcessed: 0, conceptsPriced: 0,
      startedAt: null, finishedAt: null, integrity: null, llmInputTokens: 0, llmOutputTokens: 0,
    };
  }
  const rows = conceptsForStage();
  const t = totalsForRows(rows);
  if (stage === TOUR.CATALOGOS || stage === TOUR.EQUIVALENTES) {
    return { ...base, status: 'done', conceptsPriced: t.priced, llmInputTokens: stage === TOUR.CATALOGOS ? 0 : 48200, llmOutputTokens: stage === TOUR.CATALOGOS ? 0 : 6400 };
  }
  if (stage === TOUR.SINTETICOS) return { ...base, status: 'done', conceptsPriced: t.priced };
  return base;
}

function projectForStage(): Record<string, unknown> {
  const stage = tour.stage;
  const p = { ...(casa.project as Record<string, unknown>) };
  p.isExample = false;
  if (tour.projectName) p.name = tour.projectName;
  const docsListos = stage >= TOUR.RESUMEN;
  p.totalFiles = docsListos || uploadedFile ? 1 : 0;
  p.digestStatus = docsListos ? 'ready' : 'pending';
  p.indexStatus = docsListos ? 'ready' : 'idle';
  p.chunksTotal = docsListos ? 18 : 0;
  p.chunksEmbedded = docsListos ? 18 : 0;
  p.indexedAt = docsListos ? iso : null;
  p.obraAnalysis = stage >= TOUR.CHAT ? (casa.project as Record<string, unknown>).obraAnalysis : null;
  p.summaryStatus = stage >= TOUR.CHAT ? 'ready' : 'idle';
  p.summaryAt = stage >= TOUR.CHAT ? iso : null;
  p.latestQuoteId = stage >= TOUR.ARMAR ? QUOTE : null;
  p.latestQuoteStatus = stage >= TOUR.ARMAR ? (stage === TOUR.ARMAR ? 'draft' : 'done') : null;
  const rows = conceptsForStage();
  const t = totalsForRows(rows);
  const totalConceptos = stage >= TOUR.CATALOGOS ? 35 : 0;
  p.quoteConcepts = totalConceptos;
  p.quoteConceptsPriced = t.priced;
  p.quotedPct = totalConceptos > 0 ? Math.round((t.priced / totalConceptos) * 100) : 0;
  p.quotedAmount = t.amount;
  // La exportación real la hace el usuario desde OPUS 27 (importar); el demo
  // solo la explica, no la simula.
  p.exportedToOpus = false;
  return p;
}

function filesForStage(): unknown[] {
  if (tour.stage >= TOUR.RESUMEN) return casa.files;
  if (uploadedFile) return [uploadedFile];
  return [];
}

// ── Subida simulada del PDF (la llama uploadFiles() en api/projects.ts) ──────
export function demoUploadFiles(
  files: File[],
  onProgress?: (frac: number) => void,
): Promise<{ files: ProjectFile[]; deduplicated: Array<{ id: string; duplicateOf: string }> }> {
  const pdf = casa.files[0] as unknown as ProjectFile & Record<string, unknown>;
  // SIEMPRE se muestra el PDF del ejercicio, sin importar qué archivo soltó el
  // visitante: el demo procesa la casa habitación, y mostrar el nombre del
  // archivo del visitante lo confundía (parecía que se procesó SU documento).
  void files;
  const nombre = pdf.originalName;
  return new Promise((resolve) => {
    // Barra de subida del DropZone (0 → 1).
    const pasos = [0.18, 0.42, 0.66, 0.88, 1];
    pasos.forEach((f, i) => setTimeout(() => onProgress?.(f), 180 * (i + 1)));
    setTimeout(() => {
      uploadedFile = { ...pdf, originalName: nombre, parseStatus: 'pending', parsedAt: null, chunksCount: 0, conceptsDetected: null };
      resolve({ files: [uploadedFile as ProjectFile], deduplicated: [] });
      // Lectura + clasificación + indexado con los eventos REALES del worker.
      tour.setBusy('Leyendo el documento…');
      demoBusPlay([
        [350, { type: 'parse:progress', data: { fileId: FILE_PDF, status: 'parsing', progressPct: 15 } }],
        [700, { type: 'parse:progress', data: { fileId: FILE_PDF, status: 'parsing', progressPct: 55 } }],
        [700, () => { tour.setBusy('Clasificando: Catálogo de Conceptos…'); if (uploadedFile) uploadedFile.parseStatus = 'parsing'; }],
        [50, { type: 'parse:progress', data: { fileId: FILE_PDF, status: 'parsing', progressPct: 85 } }],
        [650, () => {
          if (uploadedFile) Object.assign(uploadedFile, { parseStatus: 'ok', classification: 'catalogo', parsedPages: 5, parsedAt: iso, conceptsDetected: 35 });
          tour.setBusy('Indexando para las preguntas…');
        }],
        [50, { type: 'parse:progress', data: { fileId: FILE_PDF, status: 'ok', classification: 'catalogo', parsedPages: 5, ocrUsed: false } }],
        [350, { type: 'index:progress', data: { stage: 'chunking', fileId: FILE_PDF, started: true, chunks: 18 } }],
        [650, { type: 'index:progress', data: { stage: 'embedding', chunksTotal: 18, chunksEmbedded: 9 } }],
        [700, () => { tour.setStage(TOUR.RESUMEN); tour.setBusy(null); }],
        [50, { type: 'index:progress', data: { stage: 'embedding', done: true, projectStatus: 'ready', chunksTotal: 18, chunksEmbedded: 18 } }],
      ]);
    }, 1100);
  });
}

// ── Secuencias de las etapas largas ──────────────────────────────────────────
function playResumen() {
  tour.setBusy('Analizando los documentos…');
  demoBusPlay([
    [250, { type: 'analyze:progress', data: { stage: 'collect', filesUsed: 1, summaryStatus: 'analyzing', message: 'Recopilando documentos…' } }],
    [1300, () => tour.setBusy('La IA está redactando el resumen…')],
    [50, { type: 'analyze:progress', data: { stage: 'ai', model: 'deepseek-chat', summaryStatus: 'analyzing', detectedEntity: 'Propietario particular' } }],
    [1900, () => { tour.setStage(TOUR.CHAT); tour.setBusy(null); }],
    [100, { type: 'analyze:progress', data: { stage: 'done', summaryStatus: 'ready' } }],
  ]);
}

function playArmarHoja() {
  tour.setBusy('Armando la hoja de presupuesto…');
  demoBusPlay([
    [250, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'queued', status: 'queued', pct: 0 } }],
    [650, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'extract', status: 'running', pct: 18, message: 'Leyendo el listado de conceptos…' } }],
    [850, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'extract', status: 'running', pct: 52, conceptsTotal: 18, message: 'Detectando partidas y cantidades…' } }],
    [850, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'extract', status: 'running', pct: 86, conceptsTotal: 31 } }],
    [700, () => { tour.setStage(TOUR.CATALOGOS); tour.setBusy(null); }],
    [100, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'extract', status: 'done', pct: 100, conceptsTotal: 35, conceptsProcessed: 35, conceptsPriced: 0 } }],
  ]);
}

function playEquivalentes() {
  tour.setBusy('Comparando contra tus catálogos…');
  const tick = (n: number) => () => tour.setTicks({ pricedTick: n });
  demoBusPlay([
    [250, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'queued', status: 'queued', pct: 0 } }],
    [600, tick(5)],
    [50, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'pricing', status: 'running', pct: 14, conceptsTotal: 35, conceptsProcessed: 6, conceptsPriced: 5, message: 'Fase 1 · comparando por texto y semántica' } }],
    [850, tick(10)],
    [50, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'pricing', status: 'running', pct: 32, conceptsTotal: 35, conceptsProcessed: 12, conceptsPriced: 10 } }],
    [850, tick(16)],
    [50, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'pricing', status: 'running', pct: 50, conceptsTotal: 35, conceptsProcessed: 18, conceptsPriced: 16 } }],
    [850, () => { tick(21)(); tour.setBusy('Confirmando los dudosos con IA…'); }],
    [50, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'pricing', status: 'running', pct: 68, conceptsTotal: 35, conceptsProcessed: 25, conceptsPriced: 21, message: 'Fase 2 · confirmando la zona gris con IA' } }],
    [850, tick(26)],
    [50, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'pricing', status: 'running', pct: 86, conceptsTotal: 35, conceptsProcessed: 31, conceptsPriced: 26 } }],
    [800, () => { tick(29)(); tour.setStage(TOUR.SINTETICOS); tour.setBusy(null); }],
    [100, { type: 'quote:progress', data: { quoteId: QUOTE, stage: 'done', status: 'done', pct: 100, conceptsTotal: 35, conceptsProcessed: 35, conceptsPriced: 29, message: 'Fase 1 3.2s · Fase 2 7.8s', llmTokens: { input: 48200, output: 6400 } } }],
  ]);
}

function playSinteticos(total: number) {
  tour.setBusy('Construyendo análisis con IA…');
  const claves = (ALL_ROWS.filter((c) => c.kind !== 'group' && isSintetico(c)).map((c) => c.clave) as string[]);
  const seq: Array<[number, Parameters<typeof demoBusEmit>[0] | (() => void)]> = [
    [250, { type: 'apu_ia:progress', data: { quoteId: QUOTE, stage: 'start', conceptos_total: total, conceptos_procesados: 0, message: 'Aplicando el contexto de la obra…' } }],
  ];
  for (let i = 1; i <= total; i++) {
    const n = i;
    seq.push([900, () => tour.setTicks({ apuTick: n })]);
    seq.push([50, { type: 'apu_ia:progress', data: { quoteId: QUOTE, stage: 'in_progress', conceptos_total: total, conceptos_procesados: n, message: `Matriz de ${claves[n - 1] ?? '…'} — cuadrilla, materiales y rendimientos` } }]);
  }
  seq.push([700, () => { tour.setStage(TOUR.VER); tour.setBusy(null); }]);
  seq.push([100, { type: 'apu_ia:progress', data: { quoteId: QUOTE, stage: 'done', conceptos_total: total, conceptos_procesados: total, cost_usd: 0.0184, duration_ms: 6300 } }]);
  demoBusPlay(seq);
}

// ── Fixtures constantes ──────────────────────────────────────────────────────
const CATALOGS = [
  { id: ECOSTOS_ID, tenantId: null, kind: 'ecostos', name: 'ECOSTOS', displayName: null, description: 'Base oficial de Ecosoft — incluida con la suscripción vigente de OPUS Módulo 1', status: 'ready', resourcesCount: 79000, compuestosCount: 3001, resourcesSinUnidad: 0, resourcesSinPrecio: 0, lastSyncError: null, embeddingProgress: null, lastSyncedAt: iso, createdAt: iso, updatedAt: iso },
  { id: 'demo-cat-maestro', tenantId: TENANT_ID, kind: 'catalog', name: 'Mi catálogo maestro 2025', displayName: null, description: 'Catálogo propio del cliente (Excel importado)', status: 'ready', resourcesCount: 1240, compuestosCount: 310, resourcesSinUnidad: 0, resourcesSinPrecio: 0, lastSyncError: null, embeddingProgress: null, lastSyncedAt: iso, createdAt: iso, updatedAt: iso },
];

function usageForStage() {
  const s = tour.stage;
  // Gasto ACUMULADO por etapa — mismo desglose que narra el Asistente (AsistenteEcosoft):
  // lectura+indexado 6,200 · resumen 12,400 · chat 2,500 · armar hoja 18,900 ·
  // equivalentes 35,700 · 6 APUs IA 40,200 → total del ejercicio 115,900.
  // (armar + equivalentes = 54,600 = llmInputTokens+llmOutputTokens del quote.)
  const tokens =
    s <= TOUR.SUBIR ? 0
    : s === TOUR.RESUMEN ? 6200
    : s === TOUR.CHAT ? 18600
    : s <= TOUR.ARMAR ? 21100
    : s <= TOUR.EQUIVALENTES ? 40000
    : s === TOUR.SINTETICOS ? 75700
    : 115900;
  return {
    period: '2026-08',
    plan: 'start',
    plan_display: 'OPUS Start',
    counters: {
      projects_created: s >= TOUR.SUBIR ? 1 : 0,
      chats_sent: s >= TOUR.CONTEXTO ? 1 : 0,
      concepts_quoted: s >= TOUR.SINTETICOS ? 35 : 0,
      apus_generated: s >= TOUR.VER ? 6 : 0,
      catalogs_active: 1,
    },
    // Modelo real (bolsa única de tokens, jul 2026): los counters de IA
    // (chats/conceptos/APUs) son ESTADÍSTICA sin límite → limit null. Con un
    // número aquí la UI pintaba "Conceptos restantes este mes", que no existe
    // en la app real.
    limits: { projects_created: 10, chats_sent: null, concepts_quoted: null, apus_generated: null, catalogs_active: 3 },
    // Cuota real del plan OPUS Start (opus-catalog.json): 2,000,000 tokens/mes.
    tokens: { used: tokens, quota: 2000000, remaining: 2000000 - tokens },
  };
}

// ── Resolver: path+method(+body) → fixture según la etapa ───────────────────
function match(path: string, re: RegExp): RegExpMatchArray | null {
  return path.split('?')[0]!.match(re);
}

export function resolveDemo(path: string, method: string, body?: unknown): unknown {
  const m = method.toUpperCase();
  const p = path.split('?')[0]!;
  const stage = tour.stage;
  const b = (body ?? {}) as Record<string, unknown>;

  // Auth
  if (p === '/api/v1/auth/me') return DEMO_SESSION;
  if (p === '/api/v1/auth/tenants') return { tenants: DEMO_SESSION.availableTenants };
  if (p === '/api/v1/auth/logout') return { ok: true };

  // Proyectos
  if (p === '/api/v1/projects' && m === 'GET') {
    return { projects: stage >= TOUR.SUBIR ? [projectForStage()] : [] };
  }
  if (p === '/api/v1/projects' && m === 'POST') {
    // El visitante crea "su" proyecto: usamos el nombre que escribió.
    const nombre = typeof b.name === 'string' && b.name.trim().length > 1 ? b.name.trim() : 'Casa habitación — Ejemplo';
    tour.setProjectName(nombre);
    if (stage === TOUR.CREAR) tour.setStage(TOUR.SUBIR);
    return { project: projectForStage() };
  }
  if (match(p, /^\/api\/v1\/projects\/[^/]+$/) && m === 'GET') {
    return { project: projectForStage(), files: filesForStage(), planosPendientes: 0, planosCostoUsd: null };
  }
  if (match(p, /^\/api\/v1\/projects\/[^/]+$/) && m === 'PATCH') return { project: projectForStage() };

  // Contexto de proyecto
  if (match(p, /\/obra-context$/)) {
    if (m === 'PUT') {
      if (stage === TOUR.CONTEXTO) tour.setStage(TOUR.HOJA);
      return { ok: true, totalOutdated: 0 };
    }
    if (m === 'DELETE') return { ok: true, cleared: 0 };
    return { ...casa.contextRecord, source: stage >= TOUR.HOJA ? 'mixed' : 'deduced' };
  }

  // Resumen del concurso
  if (match(p, /\/summary\/regenerate$/) || match(p, /\/analyze$/)) {
    if (stage === TOUR.RESUMEN) playResumen();
    return { ok: true, status: 'analyzing' };
  }
  if (match(p, /\/summary$/) || match(p, /\/analysis$/)) {
    if (stage >= TOUR.CHAT) return { projectId: PRJ, status: 'ready', summaryAt: iso, error: null, analysis: casa.analysis };
    return { projectId: PRJ, status: 'idle', summaryAt: null, error: null, analysis: null };
  }

  // Chat (el stream vive en chatDemo.ts; acá solo la persistencia)
  if (match(p, /\/conversations\/[^/]+$/) && m === 'GET') {
    return { conversation: { id: 'demo-conv-1', projectId: PRJ, userId: DEMO_USER_ID, title: 'Preguntas de la obra', createdAt: iso, updatedAt: iso, lastMessageAt: iso }, messages: demoChatLog };
  }
  if (match(p, /\/conversations$/) && m === 'POST') {
    return { conversation: { id: 'demo-conv-1', projectId: PRJ, userId: DEMO_USER_ID, title: null, createdAt: iso, updatedAt: iso, lastMessageAt: null } };
  }
  if (match(p, /\/conversations$/)) {
    return { conversations: demoChatLog.length > 0 ? [{ id: 'demo-conv-1', projectId: PRJ, userId: DEMO_USER_ID, title: 'Preguntas de la obra', createdAt: iso, updatedAt: iso, lastMessageAt: iso }] : [] };
  }

  // Cotización — crear / leer
  if (match(p, /\/projects\/[^/]+\/quotes$/) && m === 'POST') {
    if (stage === TOUR.HOJA) tour.setStage(TOUR.ARMAR);
    return { quote: quoteForStage(), jobId: undefined };
  }
  if (match(p, /\/projects\/[^/]+\/quotes$/) && m === 'GET') {
    return { quotes: stage >= TOUR.ARMAR ? [quoteForStage()] : [] };
  }
  if (match(p, /\/quotes\/preview$/) && m === 'POST') {
    // planLimit/remaining null = ilimitado (el gate real es la bolsa de tokens).
    return {
      totalConcepts: 35, plan: 'start', currentUsage: 0, planLimit: null, packBalance: 0,
      effectiveLimit: null, remaining: null, fits: true, needsVision: false, visionPages: null,
      visionCost: null, monedaObra: 'MXN', monedaOrigen: 'contexto', avisoMoneda: null,
    };
  }
  if (match(p, /\/quotes\/[^/]+\/regenerate$/) && m === 'POST') {
    const opts = (b.options ?? {}) as Record<string, unknown>;
    const extractOnly = opts.extractOnly === true || b.extractOnly === true;
    if (extractOnly && stage === TOUR.ARMAR) playArmarHoja();
    else if (!extractOnly && stage === TOUR.EQUIVALENTES) { tour.setTicks({ pricedTick: 0 }); playEquivalentes(); }
    return { quote: { ...quoteForStage(), status: 'queued' }, jobId: 'demo-job-1' };
  }
  if (match(p, /\/quotes\/[^/]+\/catalogs$/) && m === 'PATCH') {
    const ids = Array.isArray(b.catalogIds) ? (b.catalogIds as string[]) : [];
    tour.setCatalogIds(ids);
    if (stage === TOUR.CATALOGOS && ids.includes(ECOSTOS_ID)) tour.setStage(TOUR.EQUIVALENTES);
    return { quote: quoteForStage() };
  }
  if (match(p, /\/quotes\/[^/]+\/source$/) && m === 'PATCH') return { quote: quoteForStage() };
  if (match(p, /\/quotes\/[^/]+\/coverage$/)) {
    return tour.catalogIds.length > 0
      ? { total: 35, sampled: 35, covered: 29, coverage: 0.83, gateMin: 0.4 }
      : { total: 35, sampled: 35, covered: 0, coverage: 0, gateMin: 0.4 };
  }
  if (match(p, /\/quotes\/[^/]+\/concepts$/)) {
    const rows = conceptsForStage();
    return { quoteId: QUOTE, total: rows.length, items: rows };
  }
  if (match(p, /\/quotes\/[^/]+\/selection$/)) return { seleccionados: 35 };
  if (match(p, /\/quotes\/[^/]+\/control$/)) return { quote: quoteForStage() };
  if (match(p, /^\/api\/v1\/quotes\/[^/]+$/) && m === 'GET') return quoteForStage();

  // APU IA — toggles, generación, runs, matrices
  if (match(p, /\/apu-ia\/concepts\/[^/]+\/requested$/) && m === 'POST') {
    const conceptId = p.split('/').slice(-2)[0]!;
    iaOverrides[conceptId] = b.requested === true;
    return { ok: true, requested: b.requested === true };
  }
  if (match(p, /\/quotes\/[^/]+\/apu-ia\/requested$/) && m === 'POST') {
    const ids = Array.isArray(b.conceptIds) ? (b.conceptIds as string[]) : null;
    const rows = conceptsForStage().filter((c) => c.kind !== 'group');
    for (const c of ids ?? rows.map((r) => r.id)) iaOverrides[c] = b.requested === true;
    return { ok: true, requested: b.requested === true, actualizados: (ids ?? rows).length };
  }
  if (match(p, /\/apu-ia\/generate$/) && m === 'POST') {
    const total = Array.isArray(b.conceptIds) && b.conceptIds.length > 0 ? (b.conceptIds as string[]).length : 6;
    if (stage === TOUR.SINTETICOS) { tour.setTicks({ apuTick: 0 }); playSinteticos(Math.min(total, 6)); }
    return { runId: RUN, jobId: 'demo-apu-job-1', conceptos_total: Math.min(total, 6), status: 'queued' };
  }
  if (match(p, /\/apu-ia\/concepts\/[^/]+\/matrix$/)) {
    const conceptId = p.split('/').slice(-2)[0]!;
    const concepto = casa.apuConceptos.find((c) => c.id === conceptId) ?? casa.apuConceptos[0];
    return { concepto };
  }
  if (match(p, /\/apu-ia\/runs\/[^/]+$/)) return { run: casa.apuRun, conceptos: casa.apuConceptos, pendingApuIa: 0 };
  if (match(p, /\/apu-ia\/runs$/)) return { runs: stage >= TOUR.VER ? [casa.apuRun] : [] };
  if (match(p, /\/matrices\/[^/]+\/(approve|reject|reopen)$/)) return { ok: true, status: 'approved' };
  if (match(p, /\/matrices\/approve-all$/)) return { ok: true, approved: casa.apuConceptos.length };
  if (match(p, /\/apu-ia\//)) return { ok: true };

  // Catálogos del proyecto (selección compartida proyecto ↔ cotización)
  if (match(p, /\/projects\/[^/]+\/catalogs$/)) {
    if (m !== 'GET') return { ok: true };
    return { items: tour.catalogIds.map((id, i) => ({ catalogId: id, priority: i })), catalogs: tour.catalogIds.map((id, i) => ({ catalogId: id, priority: i })) };
  }

  // Admin — miembros de la empresa
  if (match(p, /^\/api\/v1\/tenant\/members/)) {
    if (m !== 'GET') return { ok: true, role: 'member' };
    return {
      members: [
        { userId: DEMO_USER_ID, email: 'visitante@demo.opus.com.mx', name: 'Visitante de demostración', role: 'owner', lastLoginAt: iso },
        { userId: 'demo-user-0002', email: 'cotizador@constructorademo.mx', name: 'Carlos Cotizador', role: 'member', lastLoginAt: iso },
      ],
    };
  }

  // Usage
  if (p === '/api/v1/usage') return usageForStage();

  // Catálogos / ecostos / custom dbs
  if (match(p, /\/catalogs/)) return { items: CATALOGS, catalogs: CATALOGS };
  if (match(p, /\/ecostos/)) return { items: [], results: [] };
  if (match(p, /\/custom-dbs/)) return { items: [] };

  // Default seguro: objeto con todas las claves-array comunes vacías, para que
  // cualquier vista sin fixture específico muestre su estado vacío y NUNCA
  // crashee al leer `.length` de un wrapper inexistente.
  if (m === 'GET') {
    return {
      items: [], total: 0, conversations: [], quotes: [], projects: [], runs: [],
      rows: [], results: [], messages: [], catalogs: [], events: [], factores: [],
    };
  }
  return { ok: true };
}
